import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaymentResponse, PaymentAllocationResponse } from '../../api/types';
import { usePayment } from './hooks';
import { ReasonDialog } from './ReasonDialog';
import { OrderLabel } from './OrderLabel';
import { formatMoney, parsePaise } from './allocation';

/** Refresh the exact receipt before presenting an irreversible allocation reversal. */
export function VoidAllocationDialog({
  paymentId,
  allocationId,
  submitting,
  error,
  onCancel,
  onSubmit,
}: {
  paymentId: number;
  allocationId: number;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (reason: string) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const query = usePayment(paymentId);
  const refresh = useRef(query.refetch);
  const [verified, setVerified] = useState(false);
  const [failed, setFailed] = useState(false);
  const verify = async () => {
    setVerified(false);
    setFailed(false);
    const result = await refresh.current();
    setFailed(result.isError);
    setVerified(!result.isError);
  };
  useEffect(() => {
    let active = true;
    void refresh.current().then((result) => {
      if (active) {
        setFailed(result.isError);
        setVerified(!result.isError);
      }
    });
    return () => {
      active = false;
    };
  }, [paymentId]);
  const payment = query.data;
  const allocation = payment?.allocations.find((line) => line.id === allocationId);
  const eligible =
    verified &&
    !query.isFetching &&
    payment?.id === paymentId &&
    payment.direction === 'IN' &&
    payment.status === 'PAID' &&
    allocation?.status === 'ACTIVE' &&
    allocation.purpose === 'RECEIPT';
  return (
    <ReasonDialog
      title={t('payments.voidAllocationDialog.title')}
      actionLabel={t('payments.voidAllocationDialog.action')}
      help="cancel-payment"
      onCancel={onCancel}
      submitting={submitting}
      error={error}
      disabled={!eligible}
      onSubmit={async (reason) => {
        if (!eligible || submittingRef.current || submitting) return;
        submittingRef.current = true;
        try {
          await onSubmit(reason);
        } finally {
          submittingRef.current = false;
        }
      }}
      context={
        <>
          {!verified && !failed && <p role="status">{t('payments.voidAllocationDialog.refreshing')}</p>}
          {failed && (
            <p role="alert">
              {t('payments.voidAllocationDialog.refreshFailed')}{' '}
              <button onClick={() => void verify()}>{t('payments.voidAllocationDialog.retryReceipt')}</button>
            </p>
          )}
          {verified && !eligible && (
            <p role="alert">
              {t('payments.voidAllocationDialog.notEligible')}
            </p>
          )}
          {eligible && payment && allocation && (
            <VoidAllocationContext payment={payment} allocation={allocation} />
          )}
        </>
      }
    />
  );
}

export function VoidAllocationContext({
  payment,
  allocation,
}: {
  payment: PaymentResponse;
  allocation: PaymentAllocationResponse;
}) {
  const { t } = useTranslation();
  return (
    <div>
      <p>
        <strong>
          {t('payments.voidAllocationContext.heading', { paymentId: payment.id, allocationId: allocation.id })}
        </strong>
      </p>
      <p>
        <OrderLabel
          shipmentId={allocation.shipmentId}
          orderNumber={allocation.orderNumber}
          route={allocation.route}
          vehicleNumber={allocation.vehicleNumber}
          newTab
        />
      </p>
      {!allocation.route && <p>{t('payments.voidAllocationContext.routeUnavailable')}</p>}
      <p>
        {t('payments.voidAllocationContext.cashTds', {
          cash: formatMoney(parsePaise(allocation.amount)),
          tds: formatMoney(parsePaise(allocation.tdsAmount)),
        })}
      </p>
      <p>
        {t('payments.voidAllocationContext.cashReturned')}{' '}
        <strong>{formatMoney(parsePaise(allocation.amount))}</strong>
        {t('payments.voidAllocationContext.cashReturnedNote')}
      </p>
      <p>
        {t('payments.voidAllocationContext.creditAfter')}{' '}
        <strong>
          {formatMoney(parsePaise(payment.unallocatedCredit) + parsePaise(allocation.amount))}
        </strong>
      </p>
      <p>
        {t('payments.voidAllocationContext.onlyThis')}
      </p>
    </div>
  );
}
